import { useState } from "react";
import {
  KeyRound,
  Webhook as WebhookIcon,
  Code2,
  BookOpen,
  Copy,
  Trash2,
  Plus,
  ExternalLink,
  CheckCircle2,
} from "lucide-react";
import {
  useApiKeys,
  useCreateApiKey,
  useRevokeApiKey,
  useWebhooks,
} from "../../lib/queries";
import {
  GlassCard,
  StatCard,
  SectionTag,
  Pill,
  Button,
  Spinner,
  EmptyState,
} from "../../components/ui";
import { timeAgo } from "../../lib/format";
import type { ApiKey, Webhook } from "../../types/api";

const ENDPOINT_COUNT = 28;
const API_VERSION = "v1.0";

const SCHEMA_LINKS: { href: string; title: string; desc: string }[] = [
  {
    href: "/api/schema/swagger/",
    title: "Swagger UI",
    desc: "Interactive try-it console for every endpoint.",
  },
  {
    href: "/api/schema/redoc/",
    title: "ReDoc",
    desc: "Readable, three-pane reference documentation.",
  },
  {
    href: "/api/schema/",
    title: "Raw OpenAPI",
    desc: "Machine-readable schema (YAML) for codegen.",
  },
];

const CURL_SAMPLE = `curl https://api.pacificone.io/api/events/ \\
  -H "Authorization: Api-Key pk_live_8f2a…c9d1" \\
  -H "Accept: application/json"`;

const RESPONSE_SAMPLE = `{
  "count": 1284,
  "next": "/api/events/?limit=40&offset=40",
  "results": [
    {
      "id": 90412,
      "event_type_label": "Proximity · Pedestrian",
      "severity": "critical",
      "risk_score": 92.4,
      "status": "new",
      "site_title": "Pilbara North Pit 4",
      "emesrt_level": "L8",
      "start_time": "2026-06-16T03:11:47Z"
    }
  ]
}`;

const WEBHOOK_PAYLOAD = `{
  "event": "exception.created",
  "delivered_at": "2026-06-16T03:11:48Z",
  "data": {
    "id": 90412,
    "severity": "critical",
    "risk_score": 92.4,
    "asset_registration": "HD-785-12",
    "url": "https://app.pacificone.io/triage/90412"
  }
}`;

interface LogRow {
  ts: string;
  kind: string;
  detail: string;
  color: string;
}

const ACTIVITY_LOG: LogRow[] = [
  {
    ts: "42s ago",
    kind: "webhook.delivery",
    detail: "exception.created → 200 OK · 184ms",
    color: "var(--aurora-teal)",
  },
  {
    ts: "6m ago",
    kind: "api.request",
    detail: "GET /api/events/ · Api-Key pk_live_8f2a · 200",
    color: "var(--aurora-cyan)",
  },
  {
    ts: "23m ago",
    kind: "webhook.delivery",
    detail: "exception.classified → 200 OK · 211ms",
    color: "var(--aurora-teal)",
  },
  {
    ts: "1h ago",
    kind: "webhook.retry",
    detail: "exception.created → 503 · retry scheduled",
    color: "var(--accent)",
  },
  {
    ts: "3h ago",
    kind: "key.created",
    detail: "edge-ingest key issued · scopes: read,write",
    color: "var(--pacific)",
  },
];

export default function Developer() {
  const { data: keys, isLoading: keysLoading } = useApiKeys();
  const { data: webhooks, isLoading: hooksLoading } = useWebhooks();
  const createKey = useCreateApiKey();
  const revokeKey = useRevokeApiKey();

  const [keyName, setKeyName] = useState("");
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const activeKeys = (keys ?? []).filter((k) => !k.revoked).length;

  async function handleCreate() {
    const name = keyName.trim();
    if (!name || createKey.isPending) return;
    const created = await createKey.mutateAsync({
      name,
      scopes: ["read", "write"],
    });
    setKeyName("");
    setCopied(false);
    if (created.secret) setNewSecret(created.secret);
  }

  function copySecret(value: string) {
    void navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <SectionTag>DEVELOPER · INTEGRATION SURFACE</SectionTag>
        <h1 className="text-xl font-semibold">API Keys, Webhooks &amp; Schemas</h1>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          label="ACTIVE API KEYS"
          value={activeKeys}
          accent="var(--aurora-cyan)"
          sub="non-revoked"
        />
        <StatCard
          label="WEBHOOKS"
          value={webhooks?.length ?? 0}
          accent="var(--aurora-teal)"
        />
        <StatCard label="ENDPOINTS" value={ENDPOINT_COUNT} accent="var(--pacific)" />
        <StatCard label="API VERSION" value={API_VERSION} accent="var(--accent)" />
      </div>

      {/* API keys */}
      <GlassCard>
        <div className="mb-3 flex items-center gap-2">
          <KeyRound size={15} className="text-aurora-cyan" />
          <SectionTag>API KEYS</SectionTag>
        </div>

        {/* Create */}
        <div className="mb-4 flex flex-col gap-2 sm:flex-row">
          <input
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleCreate();
            }}
            placeholder="Key name (e.g. edge-ingest, bi-export)"
            className="flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-ink placeholder:text-slatey focus:border-aurora-cyan/50 focus:outline-none"
          />
          <Button
            variant="cyan"
            onClick={() => void handleCreate()}
            disabled={!keyName.trim() || createKey.isPending}
          >
            <span className="flex items-center gap-1.5">
              <Plus size={15} /> {createKey.isPending ? "Creating…" : "Create key"}
            </span>
          </Button>
        </div>

        {/* One-time secret banner */}
        {newSecret && (
          <div className="mb-4 rounded-lg border border-accent/50 bg-accent/5 p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-accent">
                <CheckCircle2 size={14} /> New secret — shown once
              </span>
              <button
                onClick={() => setNewSecret(null)}
                className="text-xs text-slatey transition hover:text-ink"
              >
                Dismiss
              </button>
            </div>
            <div className="flex items-center gap-2">
              <code className="mono flex-1 overflow-x-auto rounded bg-black/30 px-3 py-2 text-xs text-aurora-cyan">
                {newSecret}
              </code>
              <Button variant="cyan" onClick={() => copySecret(newSecret)}>
                <span className="flex items-center gap-1.5">
                  <Copy size={14} /> {copied ? "Copied" : "Copy"}
                </span>
              </Button>
            </div>
            <p className="mt-2 text-[11px] text-slatey">
              Store this now. PacificOne never displays the full key again — only the
              prefix is retained.
            </p>
          </div>
        )}

        {/* Keys table */}
        {keysLoading ? (
          <Spinner />
        ) : (keys?.length ?? 0) === 0 ? (
          <EmptyState>No API keys yet. Create one to start integrating.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-white/10 text-[10px] uppercase tracking-wider text-slatey">
                  <th className="py-2 pr-3 font-medium">Name</th>
                  <th className="py-2 pr-3 font-medium">Prefix</th>
                  <th className="py-2 pr-3 font-medium">Scopes</th>
                  <th className="py-2 pr-3 font-medium">Last used</th>
                  <th className="py-2 pr-3 text-right font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {(keys ?? []).map((k) => (
                  <KeyRow
                    key={k.id}
                    apiKey={k}
                    onRevoke={() => revokeKey.mutate(k.id)}
                    revoking={revokeKey.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>

      {/* Webhooks */}
      <GlassCard>
        <div className="mb-3 flex items-center gap-2">
          <WebhookIcon size={15} className="text-aurora-teal" />
          <SectionTag>WEBHOOKS</SectionTag>
        </div>
        {hooksLoading ? (
          <Spinner />
        ) : (webhooks?.length ?? 0) === 0 ? (
          <EmptyState>No webhooks configured.</EmptyState>
        ) : (
          <div className="space-y-2">
            {(webhooks ?? []).map((w) => (
              <WebhookRow key={w.id} hook={w} />
            ))}
          </div>
        )}
      </GlassCard>

      {/* OpenAPI / schemas */}
      <GlassCard>
        <div className="mb-3 flex items-center gap-2">
          <BookOpen size={15} className="text-pacific" />
          <SectionTag>OPENAPI · SCHEMAS</SectionTag>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {SCHEMA_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              target="_blank"
              rel="noreferrer"
              className="group rounded-lg border border-white/10 bg-white/5 p-3 transition hover:border-aurora-cyan/40 hover:bg-white/10"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-ink">{l.title}</span>
                <ExternalLink
                  size={14}
                  className="text-slatey transition group-hover:text-aurora-cyan"
                />
              </div>
              <p className="mt-1 text-[11px] text-slatey">{l.desc}</p>
              <code className="mono mt-2 block truncate text-[10px] text-aurora-cyan">
                {l.href}
              </code>
            </a>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-slatey">
          Generated from the live DRF schema (drf-spectacular).
        </p>
      </GlassCard>

      {/* Sample request / response */}
      <div className="grid gap-3 lg:grid-cols-2">
        <GlassCard>
          <div className="mb-3 flex items-center gap-2">
            <Code2 size={15} className="text-aurora-cyan" />
            <SectionTag>SAMPLE REQUEST</SectionTag>
          </div>
          <p className="mb-2 text-[11px] text-slatey">
            Authenticate with the <span className="mono text-ink">Api-Key</span> header.
          </p>
          <CodeBlock>{CURL_SAMPLE}</CodeBlock>
        </GlassCard>

        <GlassCard>
          <div className="mb-3 flex items-center gap-2">
            <Code2 size={15} className="text-aurora-teal" />
            <SectionTag>SAMPLE RESPONSE</SectionTag>
          </div>
          <p className="mb-2 text-[11px] text-slatey">
            <span className="mono text-ink">GET /api/events/</span> — paginated
            exception list.
          </p>
          <CodeBlock>{RESPONSE_SAMPLE}</CodeBlock>
        </GlassCard>
      </div>

      {/* Webhook payload */}
      <GlassCard>
        <div className="mb-3 flex items-center gap-2">
          <WebhookIcon size={15} className="text-pacific" />
          <SectionTag>WEBHOOK PAYLOAD · EXCEPTION.CREATED</SectionTag>
        </div>
        <CodeBlock>{WEBHOOK_PAYLOAD}</CodeBlock>
      </GlassCard>

      {/* Model / API logs */}
      <GlassCard>
        <SectionTag>MODEL · API ACTIVITY LOG</SectionTag>
        <div className="space-y-1.5">
          {ACTIVITY_LOG.map((row, i) => (
            <div
              key={i}
              className="flex items-center gap-3 rounded-lg border border-white/5 bg-white/[0.03] px-3 py-2 text-xs"
            >
              <span className="mono w-16 shrink-0 text-slatey">{row.ts}</span>
              <span
                className="mono w-36 shrink-0 truncate"
                style={{ color: row.color }}
              >
                {row.kind}
              </span>
              <span className="mono truncate text-slatey">{row.detail}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-slatey">
          Lightweight view · full delivery history is available via the audit log.
        </p>
      </GlassCard>
    </div>
  );
}

function KeyRow({
  apiKey,
  onRevoke,
  revoking,
}: {
  apiKey: ApiKey;
  onRevoke: () => void;
  revoking: boolean;
}) {
  return (
    <tr className="border-b border-white/5 last:border-0">
      <td className="py-2 pr-3 font-medium text-ink">{apiKey.name}</td>
      <td className="py-2 pr-3">
        <code className="mono text-slatey">{apiKey.prefix}…</code>
      </td>
      <td className="py-2 pr-3">
        <div className="flex flex-wrap gap-1">
          {apiKey.scopes.length === 0 ? (
            <span className="text-slatey">—</span>
          ) : (
            apiKey.scopes.map((s) => <Pill key={s}>{s}</Pill>)
          )}
        </div>
      </td>
      <td className="py-2 pr-3 text-slatey">
        {apiKey.last_used_at ? timeAgo(apiKey.last_used_at) : "never"}
      </td>
      <td className="py-2 pr-3 text-right">
        {apiKey.revoked ? (
          <Pill color="#EF4444">revoked</Pill>
        ) : (
          <Button variant="danger" onClick={onRevoke} disabled={revoking}>
            <span className="flex items-center gap-1.5">
              <Trash2 size={13} /> Revoke
            </span>
          </Button>
        )}
      </td>
    </tr>
  );
}

function WebhookRow({ hook }: { hook: Webhook }) {
  const ok = hook.last_delivery_status.toLowerCase().includes("ok") ||
    hook.last_delivery_status.startsWith("2");
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-white/5 bg-white/[0.03] px-3 py-2.5 text-xs md:flex-row md:items-center md:justify-between">
      <div className="min-w-0 flex-1">
        <code className="mono block truncate text-ink">{hook.url}</code>
        <div className="mt-1.5 flex flex-wrap gap-1">
          {hook.events.map((e) => (
            <Pill key={e} color="#22D3EE">
              {e}
            </Pill>
          ))}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Pill color={hook.enabled ? "#2DD4BF" : "#64748B"}>
          {hook.enabled ? "enabled" : "disabled"}
        </Pill>
        <Pill color={ok ? "#2DD4BF" : "#F97316"}>{hook.last_delivery_status}</Pill>
      </div>
    </div>
  );
}

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="mono overflow-x-auto rounded-lg bg-black/30 p-3 text-[11px] leading-relaxed text-slatey">
      {children}
    </pre>
  );
}
